import { ModalService } from '@modules/modal/services/modal.service';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
// Common
import { Component, Injector, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Like } from '@modules/common/types/like';
import { ViewSettingsWindowService } from '@modules/common/components/view-settings/view-settings-window.service';
import { type ViewSettingsListId } from '@modules/common/components/view-settings/view-settings-lists';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';

// Utils

// Types
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { StitchFilters } from '@modules/common/types/stitch-filters';

// Services
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { BaseStitchService } from '@modules/common/services/base-stitch.service';
import { DeleteConfirmationModalService } from '@modules/common/services/delete-confirmation-modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';

// RX
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { StitchTypeToEntityType } from '@modules/common/utils/stitch';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import type { StitchTypeToSummaryDtoType } from '@sharedlib/dtos/dto/StitchItemSummaryDtoRegistry';
import { Subject, forkJoin, switchMap } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Labels } from '../../../../i18n/labels';

@Component({
  template: '',
  standalone: false,
})
export abstract class BaseSidebarContextMenuComponent<
    ITEM_TYPE extends StitchType,
    CONTAINER_TYPE extends StitchType,
    VirtualFolder extends string,
    ITEM_SUMMARY extends StitchItemSummaryDto = StitchTypeToSummaryDtoType<ITEM_TYPE>,
    CONTAINER_SUMMARY extends StitchItemSummaryDto = StitchTypeToSummaryDtoType<CONTAINER_TYPE>,
    ITEM_DETAILS extends Stitch = StitchTypeToEntityType<ITEM_TYPE>,
    CONTAINER_DETAILS extends Stitch = StitchTypeToEntityType<CONTAINER_TYPE>,
  >
  implements OnInit, OnDestroy
{
  @Input() sidebarFilterKey: VirtualFolder;
  @Input() public tenantId: string | null | undefined;
  /** The rail row this menu was opened from: "View settings…" opens its card beside it. */
  public railOrigin: HTMLElement | undefined;

  public abstract advancedSearchStateKey: Application;
  public stitchItem: ITEM_SUMMARY;
  public containerStitchItem: CONTAINER_SUMMARY;

  protected selectedContainerStitchItemId: string;
  protected alive = new Subject<void>();
  protected readonly Labels = Labels;

  private readonly quickFormInjector = inject(Injector);
  private readonly router = inject(Router);
  private readonly viewSettings = inject(ViewSettingsWindowService);
  private readonly menuInjector = inject(Injector);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly searchService = inject(AdvancedSearchService);
  private readonly closePopoverToken = inject(CLOSE_POPOVER, { optional: true });
  private readonly deleteConfirmationModalService: DeleteConfirmationModalService =
    inject(DeleteConfirmationModalService);
  protected readonly stitchStateService: BaseAppStateService<CONTAINER_DETAILS, ITEM_DETAILS, VirtualFolder> =
    inject(BaseAppStateService);

  constructor(
    protected itemsService: BaseStitchService<ITEM_TYPE, ITEM_SUMMARY, ITEM_DETAILS, StitchFilters<ITEM_SUMMARY>>,
    protected containerItemsService: BaseStitchService<
      CONTAINER_TYPE,
      CONTAINER_SUMMARY,
      CONTAINER_DETAILS,
      StitchFilters<CONTAINER_SUMMARY>
    >,
  ) {}

  ngOnInit(): void {
    this.searchService
      .getState()
      .pipe(
        map(({ [this.advancedSearchStateKey]: { containersIds } }) => containersIds[0]),
        takeUntil(this.alive),
      )
      .subscribe((containerStitchItemId) => {
        this.selectedContainerStitchItemId = containerStitchItemId;
        this.prepareQuickItems();
      });
  }

  // ngOnChanges(changes: SimpleChanges) { //  TODO fix this
  //   if (this.sidebarFilterKey in changes) {
  //     this.prepareQuickItems();
  //   }
  // }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  public unFlagAll() {
    this.itemsService.flag({ flagged: true }, false, { tenantItems: [this.stitchItem] });
    this.containerItemsService.flag({ flagged: true }, false, { tenantItems: [this.containerStitchItem] });
  }

  public unFollowAll() {
    this.itemsService.followUp({ followed: true }, null, { tenantItems: [this.stitchItem] });
    this.containerItemsService.followUp({ followed: true }, null, { tenantItems: [this.containerStitchItem] });
  }

  public unSnoozeAll() {
    this.itemsService.snooze({ snoozed: true }, null, { tenantItems: [this.stitchItem] });
    this.containerItemsService.snooze({ snoozed: true }, null, { tenantItems: [this.containerStitchItem] });
  }

  public deleteAll(): void {
    this.itemsService.delete({ archived: true }, true, undefined, { tenantItems: [this.stitchItem] });
    this.containerItemsService.delete({ archived: true }, true, undefined, { tenantItems: [this.containerStitchItem] });
  }

  public emptyTrash(): void {
    const baseType = this.containerStitchItem.itemType;

    this.deleteConfirmationModalService
      .confirm(baseType, { isEmptyTrash: true })
      .pipe(
        switchMap(() =>
          forkJoin([
            this.itemsService.deletePermanently(
              { deleted: true },
              { skipConfirmation: true, tenantItems: [this.stitchItem] },
            ),
            this.containerItemsService.deletePermanently(
              { deleted: true },
              { skipConfirmation: true, tenantItems: [this.containerStitchItem] },
            ),
          ]),
        ),
      )
      .subscribe();
  }

  public handleQuickDetails(entity: Stitch): void {
    this.quickFormInjector
      .get(ModalService)
      .openFullForm(
        entity,
        this.quickFormInjector,
        this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId),
      );
    this.handleClose();
  }

  public handleMore(entity: ITEM_DETAILS | CONTAINER_DETAILS) {
    this.stitchStateService.setMainView(entity);
    this.handleClose();
  }

  public handleClose() {
    this.closePopoverToken.next();
  }

  /** Opens "View settings" for the list this row opens, beside the row. */
  protected openViewSettings(list: ViewSettingsListId): void {
    if (!this.railOrigin) return;
    this.viewSettings.open({ list, row: this.sidebarFilterKey, origin: this.railOrigin, injector: this.menuInjector });
  }

  public openSettings() {
    this.router.navigate([`settings/${this.advancedSearchStateKey}`]);
  }

  protected abstract stitchItemFactory(params: Like<Stitch>): ITEM_SUMMARY;
  protected abstract containerStitchItemFactory(params: Like<Stitch>): CONTAINER_SUMMARY;

  protected getCommonParams(): Like<ITEM_DETAILS | CONTAINER_DETAILS> {
    const params: Like<Stitch> = { accountId: this.tenantId };
    let date = new Date();

    switch (this.sidebarFilterKey) {
      case 'snoozed':
        date.setMinutes(date.getMinutes() + 5);

        params.snoozed = date;

        break;
      case 'followed':
        // SBUX-829: the ACCOUNT's tomorrow 9am, matching the follow-up menus.
        date = relativeDateToWorkDayStart('tomorrow', this.displayTimeZoneService.timeZone());

        params.followed = date;

        break;
      case 'flagged':
        params.flagged = true;

        break;
      case 'deleted':
      case 'archived':
        break;
    }

    return params as Like<ITEM_DETAILS | CONTAINER_DETAILS>;
  }

  private prepareQuickItems() {
    const params = this.getCommonParams();

    this.stitchItem = this.stitchItemFactory(params);
    this.containerStitchItem = this.containerStitchItemFactory(params);
  }
}
